import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Users, Send } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { EmailSettings } from "./EmailSettings";

const GROUPS = [["employee", "The employee"], ["admins", "Admins"], ["owner", "Owner"]];
const chip = (on) => `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
  on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`;

const PeoplePicker = ({ people, value, onChange, testId }) => {
  const { t, pick } = useLang();
  const [q, setQ] = useState("");
  const shown = people.filter((p) => !q || `${p.name_en} ${p.name_ar} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (email) => onChange(value.includes(email) ? value.filter((e) => e !== email) : [...value, email]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={chip(value.length > 0)} data-testid={testId}>
          <Users className="h-3.5 w-3.5" /> {value.length ? t("{count} people", { count: value.length }) : t("Choose people")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search name or email")} data-testid={`${testId}-search`}
          className="mb-2 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-900" />
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {shown.map((p) => (
            <label key={p.email} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
              <Checkbox checked={value.includes(p.email)} onCheckedChange={() => toggle(p.email)} data-testid={`${testId}-${p.email}`} />
              <span className="min-w-0 flex-1 truncate">{pick(p.name_en, p.name_ar)} <span dir="ltr" className="text-xs text-slate-400">{p.email}</span></span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Row = ({ item, s, people, onChange }) => {
  const { t, pick } = useLang();
  const [sending, setSending] = useState(false);
  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/settings/notifications/${item.key}/test`);
      toast.success(t("Test email sent to {email}", { email: data.sent_to }));
    } catch (e) { toast.error(errorMessage(e)); } finally { setSending(false); }
  };
  const toggleGroup = (g) => onChange({ to: s.to.includes(g) ? s.to.filter((x) => x !== g) : [...s.to, g] });
  return (
    <li className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 ${s.enabled ? "" : "opacity-60"}`} data-testid={`notification-${item.key}`}>
      <Switch checked={s.enabled} onCheckedChange={(enabled) => onChange({ enabled })} data-testid={`notification-${item.key}-enabled`} />
      <div className="min-w-[220px] flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
          {pick(item.label, item.label_ar)}
          {item.schedule && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400"><Clock className="h-3 w-3" />{t(item.schedule)}</span>}
        </p>
        <p className="text-xs text-slate-500">{pick(item.help, item.help_ar)}</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {GROUPS.map(([g, label]) => (
          <button key={g} type="button" disabled={!s.enabled} onClick={() => toggleGroup(g)} className={chip(s.to.includes(g))}
            data-testid={`notification-${item.key}-${g}`}>{t(label)}</button>
        ))}
        <PeoplePicker people={people} value={s.users} onChange={(users) => onChange({ users })} testId={`notification-${item.key}-people`} />
        <button type="button" onClick={sendTest} disabled={sending} title={t("Send a sample of this email to yourself")}
          data-testid={`notification-${item.key}-test`}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50">
          <Send className="h-3.5 w-3.5 rtl:-scale-x-100" /> {sending ? t("Sending…") : t("Send test")}
        </button>
      </div>
    </li>
  );
};

export const NotificationsPanel = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/notifications").then(({ data }) => setData(data)).catch((e) => toast.error(errorMessage(e))); }, []);
  const update = (key, patch) => setData((d) => ({ ...d, settings: { ...d.settings, [key]: { ...d.settings[key], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: next } = await api.put("/admin/settings/notifications", { items: data.settings });
      setData(next);
      toast.success(t("Notifications saved"));
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  if (!data) return <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />;
  return (
    <div className="space-y-4" data-testid="notifications-panel">
      <div className="flex flex-wrap items-end gap-4">
        <p className="me-auto max-w-xl text-sm text-slate-500">{t("All notifications are sent by email. Choose who gets each one: the employee it is about, admins, the owner, or specific people.")} {t("Each person gets emails in the language they last used in the app.")}</p>
        <button onClick={save} disabled={saving} data-testid="notifications-save-button"
          className="btn-press min-h-[44px] rounded-2xl bg-slate-900 px-6 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? t("Saving…") : t("Save notifications")}
        </button>
      </div>
      <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200/80 bg-white">
        {data.catalog.map((item) => (
          <Row key={item.key} item={item} s={data.settings[item.key]} people={data.people} onChange={(patch) => update(item.key, patch)} />
        ))}
      </ul>
      <EmailSettings />
    </div>
  );
};
